import {
	Form,
	FormItem,
	ReorderableList
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	ZenyaDocUpdateTileModel,
	ZenyaDocDetailsTileModel,
	UpdateZenyaDocTabModel,
	updateZenyaDocTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId } from "client/Misc/utils";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { newGuid } from "client_legacy/ndaw/guid";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditZenyaDocTabItem } from "./EditZenyaDocTabItem";

const MAX_TAB_NUMBER = 20;

type ZenyaDocEditFormData = EditTileFromData<ZenyaDocUpdateTileModel>;

const validationBuilder: IValidationConfiguration<ZenyaDocEditFormData> =
	createValidationConfiguration<ZenyaDocEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder.when(
	(formData): boolean => {
		const { columnSpan, rowSpan } = formData.dimension;
		return computeAreTabsNeeded(columnSpan, rowSpan);
	}
)
	.forCollection("tabs")
	.forItems(
		item => item.uniqueId
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

tabValidation.forScalar("resourceKind")
	.add(
		isNotNullNorUndefined()
	);

export type EditZenyaDocTileProps = {
	initialModel: ZenyaDocDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditZenyaDocTile = forwardRef(
	(props: EditZenyaDocTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<ZenyaDocEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			tabs: initialModel.tabs
		});
		const formHandler: IFormHandler<ZenyaDocEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension")
			.value;

		const areTabsNeeded: boolean = useMemo(
			(): boolean => {
				return computeAreTabsNeeded(columnSpan, rowSpan);
			},
			[columnSpan, rowSpan]
		);

		const { updateHandler } = useUpdateTile<ZenyaDocUpdateTileModel>(
			formHandler,
			updateZenyaDocTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			})
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				setFormData(
					(formState) => {
						const tabsCopy: UpdateZenyaDocTabModel[] = [...formState.tabs];
						const item: UpdateZenyaDocTabModel = tabsCopy.splice(fromIndex, 1)[0];
						tabsCopy.splice(toIndex, 0, item);

						return {
							...formState,
							tabs: tabsCopy
						};
					}
				);
			},
			[]
		);

		const addTabHandler = (): void => {
			const newTab: UpdateZenyaDocTabModel = {
				name: "",
				resourceKind: null,
				uniqueId: newGuid()
			};
			const tabsHandler: IValueInputAttachable<UpdateZenyaDocTabModel[]> = formHandler.for("tabs");

			tabsHandler.reducer
				.push(newTab);

			tabsHandler.validation
				.triggerInputFinished();
		};

		const removeTabHandler = (index: number): void => {
			const tabsHandler: IValueInputAttachable<UpdateZenyaDocTabModel[]> = formHandler.for("tabs");

			if (formHandler.for("tabs").value.length > 1) {
				tabsHandler.reducer
					.removeAt(index);

				tabsHandler.validation
					.triggerInputFinished();
			}
		};

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{areTabsNeeded && (
					<FormItem hasNoMargin>
						<ReorderableList onItemMove={itemMoveHandler}>
							{formHandler.for("tabs")
								.map((tabHandler, index) => (
									<EditZenyaDocTabItem
										key={tabHandler.value
											.uniqueId}
										onRemoveTab={() => removeTabHandler(index)}
										attachable={tabHandler}
									/>
								))
							}
						</ReorderableList>
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId={generateTrackingId("edit-zenyadoc-tile","__add-tab-button")}
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				)}
			</Form>
		);
	}
);

EditZenyaDocTile.displayName = "EditZenyaDocTile";

function computeAreTabsNeeded(columnSpan: number, rowSpan: number): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
}

function prepareModelForSaving(formData: ZenyaDocEditFormData): ZenyaDocEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const areTabsNeeded: boolean = computeAreTabsNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: areTabsNeeded ? formData.tabs : []
	};
}